import { API_ENDPOINTS } from '@/config/api';

// Auth service for token-based authentication with Azure AD
export const authService = {
  // Simple health check to verify backend connectivity
  async checkBackendHealth(): Promise<boolean> {
    try {
      const response = await fetch(API_ENDPOINTS.auth.login, {
        method: 'HEAD', // Just check if endpoint exists
        credentials: 'include',
      });
      return response.status < 500; // Accept redirects (302) as healthy
    } catch (error) {
      console.warn('Backend health check failed:', error);
      return false;
    }
  },

  // Check session validity using the auth check endpoint
  async checkSession(): Promise<{ valid: boolean; user?: any; expired?: boolean }> {
    try {
      // Import apiService to ensure consistent authentication
      const { apiService } = await import('./apiService');
      
      console.debug('Checking session validity via /auth/check endpoint');
      
      // Use apiService for consistent auth header handling
      const data = await apiService.get<{ valid: boolean; user: any }>('/auth/check', true);
      
      console.debug('Session check successful:', data);
      return { valid: true, user: data.user };
      
    } catch (error: any) {
      console.warn('Session check failed:', error);
      
      // Check if it's a 401 error with specific auth error
      if (error.message && error.message.includes('Authentication expired')) {
        return { 
          valid: false, 
          expired: true
        };
      }
      
      return { valid: false };
    }
  },

  // Validate token by making a test API call to conversations endpoint
  async validateToken(token: string): Promise<boolean> {
    try {
      const response = await fetch(`${API_ENDPOINTS.conversations.list}?limit=1`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      // Token is valid if we get 200 or any success response
      // 401 means token is invalid/expired
      return response.status !== 401;
    } catch (error) {
      console.warn('Token validation failed:', error);
      return false;
    }
  },

  // Clear local storage and redirect to login
  handleSessionExpiry(): void {
    console.log('Session expired, clearing local storage and redirecting to login');
    
    // Clear all auth-related data
    localStorage.removeItem('joblogic-gpt-token');
    localStorage.removeItem('joblogic-gpt-user');
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    
    // Redirect to login
    window.location.href = '/auth/login';
  },

  // Get user information from JWT token (backend doesn't have /auth/user endpoint)
  async getUserInfo(token: string): Promise<{ name: string; email: string; roles?: string[] }> {
    try {
      // Check if this is a session-based token (not a JWT)
      if (token.startsWith('session-') || token.startsWith('offline-session-')) {
        console.log('Session-based token detected, checking localStorage for user data');
        
        // For session tokens, try to get user data from localStorage first
        const savedUser = localStorage.getItem('joblogic-gpt-user');
        if (savedUser) {
          const userData = JSON.parse(savedUser);
          return {
            name: userData.name || userData.displayName || 'Joblogic User',
            email: userData.email || 'user@joblogic.com',
            roles: userData.roles || ['user']
          };
        }
        
        // For session tokens, we should get user data from the actual session/API
        // Don't provide hardcoded fallback values
        throw new Error('Session token found but no user data available');
      }
      
      // For JWT tokens, decode normally
      return this.decodeTokenUserInfo(token);
    } catch (error) {
      console.error('Failed to get user info from token:', error);
      // Fallback to a default user structure if token decode fails
      return {
        name: 'Joblogic User',
        email: 'user@joblogic.com',
        roles: ['user'],
      };
    }
  },

  // Decode user information from JWT token
  decodeTokenUserInfo(token: string): { name: string; email: string; roles?: string[] } {
    try {
      // Simple JWT decode (just for getting user info, not for validation)
      const payload = JSON.parse(atob(token.split('.')[1]));
      
      // Extract user information from the token payload
      // Azure AD tokens typically contain these fields
      const email = payload.email || 
                   payload.upn || 
                   payload.preferred_username || 
                   payload.unique_name ||
                   'unknown@joblogic.com';
      
      const name = payload.name || 
                  payload.given_name || 
                  `${payload.given_name || ''} ${payload.family_name || ''}`.trim() ||
                  payload.displayName ||
                  email.split('@')[0];
      
      const roles = payload.roles || payload.groups || ['user'];

      console.log('Decoded user info from token:', { name, email, roles });

      return { name, email, roles };
    } catch (error) {
      console.error('Failed to decode token:', error);
      // Fallback to a generic user - this shouldn't happen with valid Azure tokens
      return {
        name: 'Joblogic User',
        email: 'user@joblogic.com',
        roles: ['user'],
      };
    }
  },

  // Logout function that calls the backend logout endpoint
  async logout(token: string): Promise<void> {
    try {
      console.debug('Logging out via backend /auth/logout endpoint');
      
      // JWT-only logout - no cookies needed
      const response = await fetch(API_ENDPOINTS.auth.logout, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Logout failed' }));
        throw new Error(errorData.detail || `Logout failed with status: ${response.status}`);
      }

      console.log('Successfully logged out from backend');
    } catch (error) {
      console.error('Backend logout failed:', error);
      throw error;
    }
  },

  // Legacy function kept for compatibility
  async exchangeCodeForToken(code: string, state: string): Promise<any> {
    console.warn('exchangeCodeForToken is deprecated - backend handles OAuth flow');
    return Promise.reject(new Error('This function is deprecated'));
  },
};
